import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { membershipAPI, championAPI, contentAPI } from '../../actions/imsServer';

import { Search } from '../../assets/images';
import ContentArea from '../../components/contentArea';
import { ChampionshipTitle } from '../../assets/images/championshipTitle';
import Popup from '../../components/popup';
import Dropdown from '../../components/dropdown';
import Toggle from '../../components/toggle';
import Divider from '../../components/divider';
import ChampionshipCard from '../../components/championship';
import InputField from '../../components/formItem/inputField';
import { getLocalizedName } from '../../utils/base';
import { API_PAGE_SIZE } from '../../constants';

export const ChampionshipUser = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [orgLoading, setOrgLoading] = useState(false);
    const [orgError, setOrgError] = useState(null);
    const [userOrganizationIds, setUserOrganizationIds] = useState([]); // 사용자가 속한 기관 ID 목록
    const [selectedCategory, setSelectedCategory] = useState(0); // Index-based for MultiToggle
    const [filterType, setFilterType] = useState('all');
    const [selectedContent, setSelectedContent] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [contentOptions, setContentOptions] = useState([]);
    const [popup, setPopup] = useState({
        open: false,
        message: '',
        subMessage: '',
        onConfirm: null
    });

    // 삭제 관련 상태
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [championshipToDelete, setChampionshipToDelete] = useState(null);
    const [myChampionshipCards, setMyChampionshipCards] = useState([]);

    // 기관 목록 가져오기
    const fetchOrganizations = async () => {
        const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
        if (!meInfo.id) return;

        try {
            setOrgLoading(true);
            setOrgError(null);

            const response = await membershipAPI.getMemberships({
                userId: meInfo.id,
                type: 1,
                state: 0,
                expand: 'organization'
            });

            if (response?.data?.memberships) {
                const mainOrgId = meInfo.mainOrganizationId?.toString() || '';

                // 사용자가 속한 기관 ID 목록 저장
                const orgIds = [];
                response.data.memberships.forEach(membership => {
                    if (membership.organizationId) {
                        orgIds.push(String(membership.organizationId));
                    }
                });
                setUserOrganizationIds(orgIds);

                const orgList = response.data.memberships.map(m => ({
                    value: m.organizationId?.toString() || '',
                    label: m.organization?.name || t('no_organization')
                }));

                // main 기관을 맨 위로 정렬
                const sortedOrgList = orgList.sort((a, b) => {
                    const aIsMain = a.value === mainOrgId;
                    const bIsMain = b.value === mainOrgId;
                    if (aIsMain && !bIsMain) return -1;
                    if (!aIsMain && bIsMain) return 1;
                    return 0;
                });

                setOrganizations(sortedOrgList);

                // 기본 선택값 설정 (mainOrganizationId가 존재하면 우선 선택)
                if (!selectedOrganization && mainOrgId) {
                    const mainOrgExists = sortedOrgList.some(org => org.value === mainOrgId);
                    if (mainOrgExists) setSelectedOrganization(mainOrgId);
                }
            } else {
                setOrganizations([]);
                setUserOrganizationIds([]);
            }
        } catch (err) {
            console.error('기관 목록 로딩 실패:', err);
            setOrgError(err.message);
            setOrganizations([]);
        } finally {
            setOrgLoading(false);
        }
    };

    const loadChampions = async () => {
        try {

            const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
            if (!meInfo.id) return;

            // organizationId에 선택된 기관 ID 또는 사용자가 속한 기관 ID들과 0을 콤마로 연결
            const params = { state: '0,2', expand: 'ranker,content' };
            if (selectedOrganization) {
                // 선택된 기관이 있으면 해당 기관과 0만
                params.organizationId = `${selectedOrganization},0`;
            } else if (userOrganizationIds.length > 0) {
                // 선택된 기관이 없으면 사용자가 속한 모든 기관과 0 포함
                params.organizationId = `${userOrganizationIds.join(',')},0`;
            } else {
                // 기관이 없으면 0만
                params.organizationId = '0';
            }

            const allChampions = [];
            const limit = API_PAGE_SIZE;
            let offset = 0;
            let hasMore = true;
            while (hasMore) {
                const championsResponse = await championAPI.getChampions({ ...params, limit, offset });
                const pageChampions = championsResponse?.data?.champions ?? championsResponse?.champions ?? [];
                const list = Array.isArray(pageChampions) ? pageChampions : [];
                allChampions.push(...list);
                if (list.length < limit) {
                    hasMore = false;
                } else {
                    offset += limit;
                }
            }
            const champions = allChampions;

            // expand: 'content'로 포함된 콘텐츠 정보로 맵 구성
            const contentNameMap = {};
            const contentIconMap = {};
            champions.forEach(c => {
                if (c.content && c.contentIndex != null) {
                    const key = String(c.contentIndex);
                    contentNameMap[key] = getLocalizedName(c.content, i18n.language) || '';
                    if (c.content.icon) contentIconMap[key] = c.content.icon;
                }
            });

            const toCard = c => {
                const start = c.startDate ? String(c.startDate).slice(0, 10) : '';
                const end = c.endDate ? String(c.endDate).slice(0, 10) : '';
                // state 기준으로 진행중/완료 구분
                // state: 0 → 진행중, state: 2 → 완료
                const state = c.state !== undefined ? c.state : 0;
                return {
                    id: c.id,
                    type: c.type,
                    state: state,
                    // 콘텐츠 이름 매칭 (없으면 빈 문자열)
                    contentName: contentNameMap[String(c.contentIndex)] || '',
                    icon: contentIconMap[String(c.contentIndex)] || '',
                    name: c.name || t('championship'),
                    locked: !!c.password,
                    startDate: start,
                    endDate: end,
                    // 완료 체크: state 기준
                    completed: state === 2,
                    playerCount: c.playerCount,
                    // 내가 만든 챔피언십
                    isMine: c.userId === meInfo.id,
                    contentIndex: String(c.contentIndex), // 필터링을 위해 추가
                    currentWinner: ''
                };
            };

            const cards = champions.map(toCard);
            setMyChampionshipCards(cards.filter(c => c.isMine));
            setChampionshipCards(cards.filter(c => !c.isMine));
        } catch (err) {
            console.error('챔피언십 데이터 로딩 실패:', err);

            setMyChampionshipCards([]);
            setChampionshipCards([]);
        }
    };

    // 초기 로드 시 기관 목록 불러오기
    React.useEffect(() => {
        fetchOrganizations();
    }, []);

    const categories = [t('in_progress'), t('completed')];

    const filterOptions = [
        { value: 'all', label: t('view_all') },
        { value: 'ranking', label: t('ranking') },
        { value: 'tournament', label: t('tournament') },
        { value: 'content', label: t('search_by_event') },
        { value: 'name', label: t('search_by_championship_name') },
    ];

    // 콘텐츠 목록 로드 (100개씩 페이지네이션)
    React.useEffect(() => {
        const loadContents = async () => {
            try {
                const allContents = [];
                const limit = API_PAGE_SIZE;
                let offset = 0;
                let hasMore = true;
                while (hasMore) {
                    const contentsResponse = await contentAPI.getContents({ state: 0, limit, offset });
                    const list = contentsResponse?.data?.contents ?? contentsResponse?.contents ?? [];
                    const arr = Array.isArray(list) ? list : [];
                    allContents.push(...arr);
                    if (arr.length < limit) {
                        hasMore = false;
                    } else {
                        offset += limit;
                    }
                }
                const options = allContents.filter(content => content.championship === 1).map(content => ({
                    value: String(content.index),
                    label: getLocalizedName(content, i18n.language) || content.name || ''
                }));
                setContentOptions(options);
            } catch (error) {
                console.error('콘텐츠 목록 로딩 실패:', error);
                setContentOptions([]);
            }
        };
        loadContents();
    }, [i18n.language]);

    const handleDelete = (championshipId) => {
        const championship = myChampionshipCards.find(card => card.id === championshipId);
        setChampionshipToDelete(championship);
        setDeleteDialogOpen(true);
    }

    const handleDeleteConfirm = async () => {
        if (championshipToDelete) {
            try {
                await championAPI.updateChampion({
                    id: championshipToDelete.id,
                    state: 1
                });

                // 삭제 후 즉시 데이터 다시 로드
                await loadChampions();

                showPopup(t('championship_has_been_deleted'), '', () => {
                    // 팝업 확인 후 추가 작업이 필요하면 여기에
                });
            } catch (error) {
                console.error('대회 삭제 실패:', error);
                showPopup(t('failed_to_delete_championship'), '', () => { });
            }
        }
        setDeleteDialogOpen(false);
        setChampionshipToDelete(null);
    }

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setChampionshipToDelete(null);
    }

    const handleCardClick = (championshipId) => {
        navigate('/championships/user-detail', {
            state: {
                championshipId: championshipId,
                championshipData: myChampionshipCards.find(card => card.id === championshipId) ||
                    championshipCards.find(card => card.id === championshipId)
            }
        });
    }

    const [championshipCards, setChampionshipCards] = useState([]);

    // 챔피언십 데이터 로드 (기관 선택 변화에 따라)
    React.useEffect(() => {
        loadChampions();
    }, [selectedOrganization, userOrganizationIds]);

    // 필터 함수
    const filterCard = (card) => {
        // 진행중/완료 필터
        const categoryMatch = selectedCategory === 0 ? !card.completed : card.completed;
        if (!categoryMatch) return false;

        // 필터 타입별 필터링
        if (filterType === 'ranking') {
            return card.type === 0;
        }
        if (filterType === 'tournament') {
            return card.type === 1;
        }
        if (filterType === 'content') {
            return selectedContent ? card.contentIndex === selectedContent : true;
        }
        if (filterType === 'name') {
            return searchKeyword ? card.name.toLowerCase().includes(searchKeyword.toLowerCase()) : true;
        }
        // 'all' 또는 기본값
        return true;
    };

    const emptyCardsAdd = {
        empty: true,
        onButtonClick: () => navigate('/championships/user-add', { state: { organizationId: selectedOrganization } }),
        buttonText: t('create_championship'),
    };

    const emptyCards = {
        empty: true,
    };

    const showPopup = (message, subMessage = '', onConfirm) => {
        setPopup({
            open: true,
            message,
            subMessage,
            onConfirm
        });
    };

    const handleAlertClose = () => {
        setPopup(prev => ({ ...prev, open: false }));
    };

    const handleAlertConfirm = () => {
        setPopup(prev => {
            const { onConfirm } = prev;
            if (typeof onConfirm === 'function') onConfirm();
            return { ...prev, open: false };
        });
    };

    /** ChampionshipCard 기본 alignSelf: stretch → 목록은 가운데 정렬 */
    const cardListSx = { alignSelf: 'center' };

    return (
        <ContentArea sx={{ display: 'flex', flexDirection: 'column', height: '100vh', paddingBottom: 0 }}>
            {/* Fixed Header Section */}
            <Box sx={{ width: '100%', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{
                    position: "absolute",
                    top: '26px',
                    left: '50%',
                    transform: 'translateX(-50%)'
                }}>
                    <ChampionshipTitle />
                </Box>

                {/* Organization Dropdown */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                    px: 1.25,
                    mt: '110px'
                }}>
                    <Dropdown
                        options={organizations}
                        value={selectedOrganization}
                        onChange={(option) => setSelectedOrganization(option.value)}
                        placeholder={orgLoading ? t('loading_organizations') : (orgError ? t('failed_to_load_organizations') : t('select_organization'))}
                    />
                </Box>

                {/* Category Toggle */}
                <Box sx={{
                    display: 'flex',
                    height: '40px',
                    padding: '2px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                }}>
                    <Toggle
                        options={categories}
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        size="medium"
                    />
                </Box>
            </Box>

            {/* Scrollable Content Container - 토글 아래 전체 스크롤 */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                paddingBottom: '100px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}>
                {/* Content Wrapper - 카드 가운데 정렬을 위한 컨테이너 */}
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.25,
                    boxSizing: 'border-box',
                    '@media (min-width: 600px)': {
                        maxWidth: '600px',
                        width: 'auto',
                    },
                    '@media (min-width: 900px)': {
                        maxWidth: '800px',
                        width: 'auto',
                    },
                }}>
                    {/* My Championship Section */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        alignSelf: 'stretch',
                        justifyContent: 'center',
                    }}>
                        <Divider text={t('my_championships')} lineColor={theme.palette.figma.white[30]} padding='0 0px' sx={{ width: '296px' }} />
                        <Typography sx={{
                            fontSize: '14px',
                            color: theme.palette.figma.Red_didim,
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}
                            onClick={() => setIsDeleting(!isDeleting)}
                        >
                            {t('delete')}
                        </Typography>
                    </Box>

                    {/* Empty State Card */}
                    {myChampionshipCards.filter(filterCard).length > 0 ?
                        <>
                            {myChampionshipCards.filter(filterCard).map((card, index) => (
                                <ChampionshipCard
                                    key={index}
                                    {...card}
                                    sx={cardListSx}
                                    showDeleteButton={isDeleting}
                                    onDelete={() => handleDelete(card.id)}
                                    onClick={() => handleCardClick(card.id)}
                                />
                            ))}
                        </>
                        : selectedCategory === 0 ?
                            <ChampionshipCard {...emptyCardsAdd} sx={cardListSx} />
                            :
                            <ChampionshipCard {...emptyCards} sx={cardListSx} />

                    }

                    {/* Filter Section */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.25,
                        height: '50px',
                        justifyContent: 'flex-start',
                        alignSelf: 'stretch',
                    }}>
                        <Dropdown
                            options={filterOptions}
                            value={filterType}
                            onChange={(option) => {
                                setFilterType(option.value);
                                setSelectedContent('');
                                setSearchKeyword('');
                            }}
                            sx={{ width: '124px', height: '30px' }}
                        />

                        {filterType === 'content' && (
                            <Dropdown
                                options={contentOptions}
                                value={selectedContent}
                                onChange={(option) => setSelectedContent(option.value)}
                                placeholder={t('select_event')}
                                sx={{ width: '124px' }}
                            />
                        )}

                        {filterType === 'name' && (
                            <InputField
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                placeholder={t('enter_championship_name')}
                                showClearIcon={true}
                                onClear={() => setSearchKeyword('')}
                                sx={{ width: '186px' }}
                                customEndAdornment={<Search />}

                            />
                        )}
                    </Box>

                    {/* Championship Cards */}
                    {championshipCards.filter(filterCard).length > 0 ?
                        <>
                            {championshipCards.filter(filterCard).map((card, index) => (
                                <ChampionshipCard
                                    key={index}
                                    {...card}
                                    sx={cardListSx}
                                    onClick={() => handleCardClick(card.id)}
                                />
                            ))}
                        </>
                        :
                        <ChampionshipCard {...emptyCards} sx={cardListSx} />
                    }
                </Box>
            </Box>

            {/* 삭제 확인 다이얼로그 */}
            <Popup
                open={deleteDialogOpen}
                message={t('delete_championship', { name: championshipToDelete?.name || '' })}
                subMessage={t('deleted_championships_cannot_be_recovered')}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />

            {/* 알림 팝업 (삭제 성공/실패 등) */}
            <Popup
                open={popup.open}
                message={popup.message}
                subMessage={popup.subMessage}
                onCancel={handleAlertClose}
                onConfirm={handleAlertConfirm}
                confirmText={t('confirm')}
            />
        </ContentArea>
    );
};

export default ChampionshipUser;